import {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

type Kind = 'idle' | 'calibrating' | 'ok' | 'silent' | 'no-input' | 'ended' | 'error';

export type MicStatus = {
  kind: Kind;
  rms?: number;
  silentMs?: number;
  noInputMs?: number;
  error?: unknown;
};

const DEFAULT_SILENCE_MS = 2 * 60 * 1000;
const DEFAULT_NO_AUDIO_MS = 2 * 1000;
const DEFAULT_CALIBRATION_MS = 1000;
const DEFAULT_MULTIPLIER = 2;

type Options = {
  stream: MediaStream | null;
  enabled?: boolean;
  silenceMs?: number;
  noAudioMs?: number;
  calibrationMs?: number;
  multiplier?: number;
};

type Decision =
  | { kind: 'active'; rms: number }
  | { kind: 'no-input'; rms: number; noInputFor: number }
  | { kind: 'silent'; rms: number; silentFor: number }
  | { kind: 'ok'; rms: number };

function computeRms(analyser: AnalyserNode, data: Uint8Array<ArrayBuffer>) {
  analyser.getByteTimeDomainData(data);
  let sum = 0;
  for (let i = 0; i < data.length; i += 1) {
    const v = (data[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / data.length);
}

function avg(nums: number[]) {
  if (nums.length === 0) return 0;
  let s = 0;
  for (let i = 0; i < nums.length; i += 1) s += nums[i];
  return s / nums.length;
}

function computeThreshold(samples: number[], multiplier: number) {
  return Math.max(avg(samples) * multiplier, 0.01);
}

function createAudioGraph(stream: MediaStream) {
  const ctx = new AudioContext();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const data: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(analyser.fftSize));
  return { ctx, analyser, data };
}

type DecideMicStatusArgs = {
  rms: number;
  now: number;
  threshold: number;
  silenceMs: number;
  noAudioMs: number;
  silentSince: number | null;
  noInputSince: number | null;
};

function decideMicStatus({
  rms,
  now,
  threshold,
  silenceMs,
  noAudioMs,
  silentSince,
  noInputSince,
}: DecideMicStatusArgs) {
  const veryLowThreshold = threshold * 0.3;

  // Active sound/speech
  if (rms > threshold) {
    return {
      decision: { kind: 'active' as const, rms },
      next: { silentSince: null as number | null, noInputSince: null as number | null },
    };
  }

  let nextNoInputSince = noInputSince;
  let nextSilentSince = silentSince;

  // Fast "No Audio Detected"
  if (rms < veryLowThreshold) {
    if (nextNoInputSince == null) nextNoInputSince = now;
    const noInputFor = now - nextNoInputSince;

    if (noInputFor >= noAudioMs) {
      return {
        decision: { kind: 'no-input' as const, rms, noInputFor },
        next: { silentSince: nextSilentSince, noInputSince: nextNoInputSince },
      };
    }
  } else {
    nextNoInputSince = null;
  }

  // Normal silence
  if (nextSilentSince == null) nextSilentSince = now;
  const silentFor = now - nextSilentSince;

  if (silentFor >= silenceMs) {
    return {
      decision: { kind: 'silent' as const, rms, silentFor },
      next: { silentSince: nextSilentSince, noInputSince: nextNoInputSince },
    };
  }

  return {
    decision: { kind: 'ok' as const, rms },
    next: { silentSince: nextSilentSince, noInputSince: nextNoInputSince },
  };
}

function applyDecision(setStatus: Dispatch<SetStateAction<MicStatus>>, decision: Decision) {
  switch (decision.kind) {
    case 'active':
      setStatus({ kind: 'ok', rms: decision.rms });
      break;
    case 'no-input':
      setStatus({ kind: 'no-input', rms: decision.rms, noInputMs: decision.noInputFor });
      break;
    case 'silent':
      setStatus({ kind: 'silent', rms: decision.rms, silentMs: decision.silentFor });
      break;
    case 'ok':
      setStatus({ kind: 'ok', rms: decision.rms });
      break;
    default:
      // do nothing
  }
}

function runForDuration(args: {
  durationMs: number;
  isPaused: () => boolean;
  onSample: () => void;
  onDone: () => void;
  schedule: (fn: () => void) => void;
}) {
  const { durationMs, isPaused, onSample, onDone, schedule } = args;

  const start = Date.now();

  const step = () => {
    if (isPaused()) {
      schedule(step);
      return;
    }

    onSample();

    if (Date.now() - start >= durationMs) {
      onDone();
      return;
    }

    schedule(step);
  };

  step();
}

export function useSilenceDetection({
  stream,
  enabled = true,
  silenceMs = DEFAULT_SILENCE_MS,
  noAudioMs = DEFAULT_NO_AUDIO_MS,
  calibrationMs = DEFAULT_CALIBRATION_MS,
  multiplier = DEFAULT_MULTIPLIER,
}: Options): MicStatus {
  const [status, setStatus] = useState<MicStatus>({ kind: 'idle' });

  const rafRef = useRef<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);

  const thresholdRef = useRef<number>(0.01);
  const silentSinceRef = useRef<number | null>(null);
  const noInputSinceRef = useRef<number | null>(null);

  const schedule = (fn: () => void) => {
    rafRef.current = requestAnimationFrame(fn);
  };

  const cancelRaf = () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const closeCtx = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctxRef.current = null;
    ctx.close().catch(() => {});
  };

  const resetTimers = () => {
    silentSinceRef.current = null;
    noInputSinceRef.current = null;
  };

  const startMonitoring = (params: {
    track: MediaStreamTrack;
    analyser: AnalyserNode;
    data: Uint8Array<ArrayBuffer>;
    silenceMs: number;
    noAudioMs: number;
  }) => {
    const { track, analyser, data, silenceMs, noAudioMs } = params;

    resetTimers();

    const tick = () => {
      if (document.hidden) {
        resetTimers();
        schedule(tick);
        return;
      }

      if (track.readyState === 'ended') {
        setStatus({ kind: 'ended' });
        return;
      }

      const rms = computeRms(analyser, data);
      const now = Date.now();

      const { decision, next } = decideMicStatus({
        rms,
        now,
        threshold: thresholdRef.current,
        silenceMs,
        noAudioMs,
        silentSince: silentSinceRef.current,
        noInputSince: noInputSinceRef.current,
      });

      silentSinceRef.current = next.silentSince;
      noInputSinceRef.current = next.noInputSince;

      applyDecision(setStatus, decision);
      schedule(tick);
    };

    tick();
  };

  const runCalibration = (params: {
    track: MediaStreamTrack;
    analyser: AnalyserNode;
    data: Uint8Array<ArrayBuffer>;
    calibrationMs: number;
    multiplier: number;
    silenceMs: number;
    noAudioMs: number;
  }) => {
    const { track, analyser, data, calibrationMs, multiplier, silenceMs, noAudioMs } = params;

    setStatus({ kind: 'calibrating' });

    const samples: number[] = [];

    runForDuration({
      durationMs: calibrationMs,
      isPaused: () => document.hidden,
      schedule,
      onSample: () => {
        samples.push(computeRms(analyser, data));
      },
      onDone: () => {
        thresholdRef.current = computeThreshold(samples, multiplier);
        startMonitoring({ track, analyser, data, silenceMs, noAudioMs });
      },
    });
  };

  useEffect(() => {
    // clean any previous run
    cancelRaf();
    closeCtx();

    if (!enabled || !stream) {
      setStatus({ kind: 'idle' });
      return undefined;
    }

    const track = stream.getAudioTracks()[0];
    if (!track) {
      setStatus({ kind: 'error', error: new Error('No audio track') });
      return undefined;
    }

    if (track.readyState === 'ended') {
      setStatus({ kind: 'ended' });
      return undefined;
    }

    const handleEnded = () => setStatus({ kind: 'ended' });
    track.addEventListener('ended', handleEnded);

    const { ctx, analyser, data } = createAudioGraph(stream);
    ctxRef.current = ctx;

    runCalibration({
      track,
      analyser,
      data,
      calibrationMs,
      multiplier,
      silenceMs,
      noAudioMs,
    });

    return () => {
      track.removeEventListener('ended', handleEnded);
      cancelRaf();
      closeCtx();
    };
  }, [stream, enabled, silenceMs, noAudioMs, calibrationMs, multiplier]);

  return status;
}
